// `onair.json` — el artista llega hecho, y la web deja de salir a buscarlo.
//
// Contrato: `EMISORA_WEB_INSTRUCCIONES.md` § `/emisora/onair.json`, leído en
// vivo el 25.09.26. Una sola petición trae la canción que suena y las cuatro
// siguientes, **cada una con su artista ya resuelto** por el servidor.
//
// 🔴 **Lo que esto sustituye.** Hasta hoy, cada cambio de canción disparaba
// desde el navegador de cada oyente: el cálculo del slug, la ficha del
// servidor, TheAudioDB —dos veces, porque la foto y la bio se pedían en
// paralelo y las dos fallaban la caché— y Last.fm. Ahora es una petición, y
// ninguna sale de `listen.astra.fm`.
//
// Y con ellas se van tres problemas que no eran de rendimiento:
//
//   · La clave de TheAudioDB estaba **muerta** —la `2` devuelve 404 desde hace
//     tiempo—, así que ese escalón fallaba en cada canción y de paso castigaba
//     al cortacircuitos de `fetchWithRetry`, que acababa cortando el host y
//     con él peticiones que sí habrían funcionado.
//   · La clave de Last.fm **viajaba en el bundle publicado**. Comprobado el
//     25.09.26: se sacaba del JavaScript de producción y funcionaba.
//   · El slug se calculaba aquí a partir del nombre que manda la emisión, que
//     no siempre es el del índice. Ahora lo manda el servidor.
//
// ⚠️ **Esto NO detecta el cambio de canción.** Eso sigue en `useNowPlaying`
// con `/api/nowplaying/3` y su temporizador. Esto solo sustituye lo que se
// pedía **después** de saber qué suena.

const URL_ONAIR = "https://listen.astra.fm/emisora/onair.json";

/**
 * Lo último que llegó bien.
 *
 * 🔴 **Se guarda para no vaciar la pantalla cuando el servidor falla.** El §3
 * del contrato dice que ante un error se mantiene lo que había; y el aviso del
 * 25.09.26 avisa de un caso concreto y esperable: justo después de un
 * despliegue suyo, `onair.json` puede responder **502 durante un minuto**
 * mientras el microservicio se prepara.
 *
 * ⚠️ En ese minuto **no se vuelve a TheAudioDB**. Ese camino ya no existe, y
 * reabrirlo «por si acaso» es cómo se acumulan las caídas que nadie quita.
 */
let ultimo = null;

/** Para no pedirlo dos veces a la vez cuando coinciden dos disparos. */
let enVuelo = null;

// 🔴 **Las canciones que ya han pasado por `onair.json`, recordadas.** El fichero
// solo trae lo que suena en el servidor y las cuatro siguientes, **no lo que
// acaba de sonar**, y el audio de cada oyente va de 12 a más de 40 segundos por
// detrás del servidor. Así que cuando una canción llega al oído, el servidor
// puede haber pasado ya a la siguiente y ella haber salido del fichero.
//
// Lo vio Pablo el 03.10.26: sonaba Sleaford Mods y la ficha seguía con la foto
// y la bio de Bowery Electric, la canción anterior. Sleaford Mods no estaba ya
// en `onair.json`, `buscaEnOnAir` no la encontraba y la regla de «sin dato no se
// toca la pantalla» dejaba al artista de antes.
//
// Toda canción ha estado antes en `siguientes`, así que guardándolas al llegar
// el artista ya está resuelto cuando suena. Se guardan las últimas 40 —unas dos
// horas de emisión—, que sobra para cualquier retraso.
const recordadas = [];
const MAX_RECORDADAS = 40;

function recordar(datos) {
  for (const c of [datos.ahora, ...(datos.siguientes || [])]) {
    if (!c?.artist) continue;
    const i = recordadas.findIndex((r) => r.artist === c.artist && r.title === c.title);
    if (i !== -1) recordadas.splice(i, 1);
    recordadas.unshift(c);
  }
  recordadas.length = Math.min(recordadas.length, MAX_RECORDADAS);
}

/**
 * Baja `onair.json`.
 *
 * @returns {Promise<object|null>} lo que sirve el servidor, o `null` si no
 *   respondió. **`null` no es «no hay nada»**: es «no he podido preguntar», y
 *   quien llama debe dejar la pantalla como estaba.
 */
export async function pideOnAir() {
  if (enVuelo) return enVuelo;

  // ⚠️ `fetch` a secas, sin `cache`: el navegador manda `If-None-Match` solo y
  // el servidor contesta 304 cuando no ha cambiado. Forzar la caché desde aquí
  // ignoraría su política, que se decide en un sitio. Mismo criterio que
  // `editorialArtists.js`.
  enVuelo = fetch(URL_ONAIR)
    .then((r) => (r.ok ? r.json() : null))
    .then((datos) => {
      if (datos && typeof datos === "object") {
        ultimo = datos;
        recordar(datos);
      }
      return datos;
    })
    .catch(() => null)
    .finally(() => {
      enVuelo = null;
    });

  return enVuelo;
}

/** Lo último que llegó bien, sin pedir nada. */
export function ultimoOnAir() {
  return ultimo;
}

/**
 * Busca en lo que ya tenemos el artista de una canción concreta.
 *
 * Mira en `ahora` y en las cuatro `siguientes`, así que **el cambio de canción
 * no cuesta una petición**: cuando entra la que venía detrás, su artista ya
 * estaba en memoria.
 *
 * ⚠️ Es también la pieza que necesita la sincronía por ID3, cuando se monte:
 * el contrato dice que hay que buscar el `artist` + `title` del audio en
 * `ahora` o en `siguientes`. Por eso compara por los dos campos y no solo por
 * el artista: en un disco doble puede sonar el mismo nombre dos veces
 * seguidas con fichas distintas.
 *
 * @param {string} artista tal como lo manda la emisión
 * @param {string} [titulo] si se da, tiene que coincidir también
 */
export function buscaEnOnAir(artista, titulo = "") {
  const datos = ultimo;
  if (!datos || !artista) return null;

  const igual = (a, b) =>
    String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

  // Primero lo de ahora, después lo recordado: si el mismo artista suena en el
  // fichero de ahora, manda esa versión, que es la más fresca.
  const candidatos = [datos.ahora, ...(datos.siguientes || []), ...recordadas].filter(
    Boolean,
  );

  const encaje = candidatos.find(
    (c) => igual(c.artist, artista) && (!titulo || igual(c.title, titulo)),
  );
  if (encaje) return encaje;

  // Sin título que cuadre, vale el nombre: es mejor enseñar la ficha correcta
  // del artista que quedarse sin nada porque el título venía con un sufijo.
  return candidatos.find((c) => igual(c.artist, artista)) || null;
}

/**
 * La canción que el servidor dice que suena **ahora**.
 *
 * ⚠️ `ahora` viene `null` mientras suena una cuña, un short o un separador.
 * Eso no es un fallo y aquí no se disimula: quien llama ya sabe qué hacer con
 * los separadores —`buildUnifiedPlaybackContext` de `useNowPlaying` resuelve
 * desde el 26.08.26 cuál es la canción real que hay que enseñar— y taparlo
 * aquí duplicaría esa decisión en dos sitios.
 */
export function loQueSuena() {
  return ultimo?.ahora || null;
}

/**
 * Normaliza el artista resuelto a lo que pintan las vistas.
 *
 * El contrato manda `foto` y `bio` como objetos con su `de`, su `credito` y su
 * `enlace`; aquí se aplanan a los nombres que ya usan Radio y la portada, para
 * no arrastrar el cambio de forma hasta la plantilla.
 *
 * ⚠️ **`foto.url` puede venir `null`** —solo si tampoco hay carátula—, y
 * entonces quien llama pone su marcador de posición. Y la **bio puede venir
 * `null` a propósito**: cuando el servidor tiene dudas de homónimo no usa nada
 * de fuera, porque es mejor no decir nada que contar la vida de otro grupo.
 * Eso es exactamente lo que se busca; no se rellena con un respaldo.
 */
export function aplanaArtista(cancion) {
  const a = cancion?.artista;
  if (!a) return null;

  const foto = a.foto || {};
  const bio = a.bio || {};

  return {
    slug: a.slug || "",
    nombre: a.nombre || cancion.artist || "",
    // `false` cuando el nombre no está en el índice: el slug es el calculado y
    // puede no tener ficha. Las vistas lo usan para no ofrecer una ficha vacía.
    enBiblioteca: a.enBiblioteca !== false,

    imagen: foto.url || "",
    imagenDe: foto.de || "",
    imagenCredito: foto.credito || "",
    imagenEnlace: foto.enlace || "",

    bio: bio.texto || "",
    bioDe: bio.de || "",
    bioCredito: bio.credito || "",
    bioEnlace: bio.enlace || "",

    trayectoria: a.trayectoria || null,
    genero: a.genero || "",
    estilo: a.estilo || "",
    sello: a.sello || "",
    miembros: a.miembros || null,
    links: a.links || null,
  };
}

// ── El ritmo de una canción (24.10.26) ──
//
// Contrato: `EMISORA_WEB_INSTRUCCIONES.md` § 2 «ritmo», leído en vivo el
// 24.10.26. `ritmo: { bpm, pulso, golpes }` en `ahora` de `onair.json` y en
// cada canción de `historial.json`, o `null` si aún no está medida. **No viene
// en `siguientes`.** Lo pidió el front para el visor de Radio
// (`PETICION_RITMO_DE_CADA_CANCION` en `astra-fm/temp`).
//
// 🔴 **Por qué se mira también el historial.** El audio de cada oyente va de 12
// a más de 40 s por detrás del servidor (arriba, `recordadas`): cuando una
// canción empieza a oírse, en el servidor ya puede haber dejado `ahora` y estar
// en el historial. Por orden: lo recordado, `onair.json` y `historial.json`.
const URL_HISTORIAL = "https://listen.astra.fm/emisora/historial.json?limit=10";

const limpio = (t) =>
  String(t || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
const esLaMisma = (c, artista, titulo) =>
  c && limpio(c.artist) === limpio(artista) && limpio(c.title) === limpio(titulo);

/**
 * El `ritmo` que manda el servidor para una canción.
 *
 * @returns {Promise<{bpm:number,pulso:boolean,golpes:number}|null>} `null` si
 *   no está medida o no se ha podido preguntar: entonces el visor escucha.
 */
export async function ritmoDe(artista, titulo) {
  if (!artista || !titulo) return null;
  const enMemoria = () => {
    const d = ultimo;
    const c = [d?.ahora, ...recordadas].find((x) => esLaMisma(x, artista, titulo) && x.ritmo);
    return c?.ritmo || null;
  };
  let r = enMemoria();
  if (r) return r;
  await pideOnAir();
  r = enMemoria();
  if (r) return r;
  const h = await fetch(URL_HISTORIAL)
    .then((x) => (x.ok ? x.json() : null))
    .catch(() => null);
  const c = (h?.canciones || []).find((x) => esLaMisma(x, artista, titulo));
  return c?.ritmo || null;
}
